import { Reference } from '@apollo/client'

import { PaginatedGroupsFieldPolicyOptions, PaginatedGroupWithDocs } from '~apollo/paginatedGroupsFieldPolicy'
import { TasksGroupByKeys, TasksDocsSortKeys, TaskStatusTypes } from '~generated/types'

// TODO
const getTaskGroupKeyFromRefCallbacks: Record<TasksGroupByKeys, (ref, readField) => string[]> = {
  [TasksGroupByKeys.STATUS]: (ref, readField) => [readField('status', ref)],
  [TasksGroupByKeys.TAGS]: (ref, readField) => {
    const tags = readField('tags', ref) as Reference[]
    return tags.map((tagRef) => readField('label', tagRef))
  },
  [TasksGroupByKeys.ASSIGNEE]: (ref, readField) => {
    const assigneeRef = readField('assignee', ref) as Reference
    if (!assigneeRef) {
      return []
    }
    return [readField('name', assigneeRef) || readField('email', assigneeRef)]
  },
}

export function getTaskGroupKeysFromRef(ref, sort, readField): string[] {
  return getTaskGroupKeyFromRefCallbacks[sort.groupBy](ref, readField)
}

const tasksDocsSortComparers = {
  [TasksDocsSortKeys.STATUS]: (a: any, b: any, readField) => {
    const statusOrderMap = {
      [TaskStatusTypes.NOT_STARTED]: 0,
      [TaskStatusTypes.IN_PROCESS]: 1,
      [TaskStatusTypes.IN_REVIEW]: 2,
      [TaskStatusTypes.DONE]: 3,
      [TaskStatusTypes.CANCELED]: 4,
      [TaskStatusTypes.DELAYED]: 5,
    }
    return statusOrderMap[readField('status', a)] - statusOrderMap[readField('status', b)]
  },
  [TasksDocsSortKeys.ASSIGNEE]: (a: any, b: any, readField) => {
    const assigneeARef = readField('assignee', a)
    const assigneeBRef = readField('assignee', b)

    if (!assigneeARef && !assigneeBRef) return 0
    // tasks without assignee should be in the end
    if (!assigneeARef) return 1
    if (!assigneeBRef) return -1

    const displayNameA = readField('name', assigneeARef) ?? readField('email', assigneeARef)
    const displayNameB = readField('name', assigneeBRef) ?? readField('email', assigneeBRef)

    if (displayNameA === displayNameB) return 0

    return displayNameA.localeCompare(displayNameB)
  },
  [TasksDocsSortKeys.DUE_DATE]: (a, b, readField) => {
    const dueDateA = readField('dueDate', a)
    const dueDateB = readField('dueDate', b)

    if (dueDateA === dueDateB) return 0
    // tasks without dueDate should be in the end
    if (!dueDateA) return 1
    if (!dueDateB) return -1

    return dueDateA > dueDateB ? 1 : -1
  },
}

export const tasksGroupsSortComparers = {
  [TasksDocsSortKeys.STATUS]: (a: PaginatedGroupWithDocs, b: PaginatedGroupWithDocs, readField) => {
    const statusOrderMap = {
      [TaskStatusTypes.NOT_STARTED]: 0,
      [TaskStatusTypes.IN_PROCESS]: 1,
      [TaskStatusTypes.IN_REVIEW]: 2,
      [TaskStatusTypes.DONE]: 3,
      [TaskStatusTypes.CANCELED]: 4,
      [TaskStatusTypes.DELAYED]: 5,
    }
    const [firstDocA] = a.docs
    const [firstDocB] = b.docs

    return statusOrderMap[readField('status', firstDocA)] - statusOrderMap[readField('status', firstDocB)]
  },
  [TasksDocsSortKeys.ASSIGNEE]: (a: PaginatedGroupWithDocs, b: PaginatedGroupWithDocs, readField) => {
    const [firstDocA] = a.docs
    const [firstDocB] = b.docs
    const assigneeARef = readField('assignee', firstDocA)
    const assigneeBRef = readField('assignee', firstDocB)

    if (!assigneeARef && !assigneeBRef) return a.key.localeCompare(b.key)
    // tasks without assignee should be in the end
    if (!assigneeARef) return 1
    if (!assigneeBRef) return -1

    const displayNameA = readField('name', assigneeARef) ?? readField('email', assigneeARef)
    const displayNameB = readField('name', assigneeBRef) ?? readField('email', assigneeBRef)

    if (displayNameA === displayNameB) return a.key.localeCompare(b.key)

    return displayNameA.localeCompare(displayNameB)
  },
  [TasksDocsSortKeys.DUE_DATE]: (a: PaginatedGroupWithDocs, b: PaginatedGroupWithDocs, readField) => {
    const [firstDocRefA] = a.docs
    const [firstDocRefB] = b.docs

    const dueDateA = readField('dueDate', firstDocRefA)
    const dueDateB = readField('dueDate', firstDocRefB)
    if (dueDateA === dueDateB) return a.key.localeCompare(b.key)

    // tasks without dueDate should be in the end
    if (!dueDateA) return 1
    if (!dueDateB) return -1

    return dueDateA > dueDateB ? 1 : -1
  },
}

export enum TasksIntegrationProductName {
  JIRA = 'JIRA',
  ASANA = 'ASANA',
  LINEAR = 'LINEAR',
  MONDAY = 'MONDAY',
  PRODUCTBOARD = 'PRODUCTBOARD',
  ROADMUNK = 'ROADMUNK',
  TRELLO = 'TRELLO',
  WRIKE = 'WRIKE',
  CLICKUP = 'CLICKUP',
}

export const tasksFieldPolicyOptions: PaginatedGroupsFieldPolicyOptions<TasksGroupByKeys, TasksDocsSortKeys> = {
  getGroupKeysFromDocRef: getTaskGroupKeysFromRef,
  groupsSortComparers: tasksGroupsSortComparers,
  docsSortComparers: tasksDocsSortComparers,
}
